import { useCallback, useEffect, useMemo, useState } from "react"

import api, { getEnterpriseId } from "../../../lib/api"
import { getStoredUser } from "../../../services/auth"
import { emitSnackbar } from "../../../lib/snackbar-emitter"
import { loader } from "../../../lib/loader"
import manageErrors from "../../../lib/error-management"

const PRODUCTS_PATH = (enterpriseId) => `enterprises/${enterpriseId}/scout_products/`

const normalizeCollection = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  if (Array.isArray(data?.data)) return data.data
  return []
}

const parseApiItem = (item = {}) => ({
  id: item.id,
  name: item.name || "",
  description: item.description || "",
})

const toApiPayload = ({ name, description, accountcode }) => ({
  name: name?.trim(),
  description: description?.trim(),
  accountcode: accountcode || "",
})

export const useProductsAndServices = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const enterpriseId = getEnterpriseId()
  const user = getStoredUser()
  const accountcode =
    user?.profile?.enterprise?.accountcode ||
    user?.profile?.enterprise?.id ||
    user?.enterprise?.id ||
    ""

  const load = useCallback(async () => {
    if (!enterpriseId) return

    setLoading(true)
    loader.on()

    try {
      const response = await api.get(PRODUCTS_PATH(enterpriseId))
      if (response.status === 200) {
        const list = normalizeCollection(response.data)
        setItems(list.map(parseApiItem))
      } else {
        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
      }
    } catch (error) {
      emitSnackbar({ message: "Erro ao carregar os produtos e serviços.", severity: "error" })
    } finally {
      loader.off()
      setLoading(false)
    }
  }, [enterpriseId])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(
    async (payload) => {
      const isEdit = Boolean(payload?.id)

      try {
        loader.on()
        const payloadWithAccount = {
          ...payload,
          accountcode,
        }

        const response = isEdit
          ? await api.patch(`${PRODUCTS_PATH(enterpriseId)}${payload.id}/`, toApiPayload(payloadWithAccount))
          : await api.post(PRODUCTS_PATH(enterpriseId), toApiPayload(payloadWithAccount))

        if (response.status === 200 || response.status === 201) {
          emitSnackbar({
            message: isEdit
              ? "Produto ou serviço atualizado com sucesso."
              : "Produto ou serviço criado com sucesso.",
            severity: "success",
          })
          await load()
          return true
        }

        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
        return false
      } catch (error) {
        const serverError = error?.response?.data || (error?.message && String(error.message)) || ''
        emitSnackbar({
          message: `Erro ao salvar o produto ou serviço.${serverError ? ` (${serverError})` : ""}`,
          severity: "error",
        })
        return false
      } finally {
        loader.off()
      }
    },
    [accountcode, enterpriseId, load],
  )

  const remove = useCallback(
    async (item) => {
      if (!item?.id) return false

      try {
        loader.on()
        const response = await api.delete(`${PRODUCTS_PATH(enterpriseId)}${item.id}/`)
        if (response.status === 204) {
          emitSnackbar({ message: "Produto ou serviço excluído com sucesso.", severity: "success" })
          await load()
          return true
        }

        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
        return false
      } catch (error) {
        emitSnackbar({ message: "Erro ao excluir o produto ou serviço.", severity: "error" })
        return false
      } finally {
        loader.off()
      }
    },
    [enterpriseId, load],
  )

  const actions = useMemo(
    () => ({
      setEditing,
      setDeleting,
      setItems,
      setLoading,
    }),
    [],
  )

  return {
    items,
    loading,
    editing,
    deleting,
    setEditing,
    setDeleting,
    load,
    save,
    remove,
    actions,
  }
}
